import React, { createContext, useContext, useState, useRef, ReactNode, useEffect, useMemo } from 'react';
import { endOfWeek, eachDayOfInterval, format, isSameDay } from 'date-fns';
import { Views } from 'react-big-calendar';
import invariant from 'invariant';

import { useFilters } from './FiltersContext';

interface EventPositionContextType {
	eventPositions: Record<string, number>;
	setEventPositions: React.Dispatch<React.SetStateAction<Record<string, number>>>;
	eventRefs: React.MutableRefObject<Record<string, HTMLElement | null>>;
}

const EventPositionContext = createContext<EventPositionContextType | undefined>(undefined);

interface EventProviderProps {
	children: ReactNode;
}

export const EventPositionProvider: React.FC<EventProviderProps> = ({ children }) => {
	const { filteredEvents: events, currentView, currentMonth } = useFilters();
	const [eventPositions, setEventPositions] = useState<Record<string, number>>({});
	const eventRefs = useRef<Record<string, HTMLElement | null>>({});

	useEffect(() => {
		//Reset positions on Filtering
		setEventPositions({});
	}, [events]);

	useEffect(() => {
		//Reset positions on Filtering
		setEventPositions({});
	}, [events]);

	useEffect(() => {
		if (currentView !== Views.MONTH) return;
		const timerId = setTimeout(() => {
			if (eventRefs.current) {
				// Filter out the current month refs and remove null/undefined
				const validRefs = Object.keys(eventRefs.current)
					.filter((key) => eventRefs.current[key] !== null && eventRefs.current[key] !== undefined)
					.reduce((acc, key) => {
						return { ...acc, [key]: eventRefs.current[key] };
					}, {});
				// Step 1: Group events by weekSlot
				const groupedByWeek = {};
				Object.keys(validRefs).forEach((key) => {
					const [eventId, weekSlot] = key.split('#');

					const weekStartDate = new Date(weekSlot);
					if (!groupedByWeek[weekSlot]) {
						groupedByWeek[weekSlot] = {};
					}
					// Add the event data (eventId, DOM reference) to the week slot group
					groupedByWeek[weekSlot][eventId] = {
						eventId,
						ref: validRefs[key],
						weekSlot: weekStartDate,
					};
				});

				// Step 2: Process each week and day inside that week
				Object.keys(groupedByWeek).forEach((weekStartDateStr) => {
					const weekStartDate = new Date(weekStartDateStr);
					const weekEndDate = endOfWeek(weekStartDate);

					// Generate all days in this week
					const weekDays = eachDayOfInterval({
						start: weekStartDate,
						end: weekEndDate,
					});
					const eventsInPresentWeek = groupedByWeek[weekStartDateStr];
					// Step 3: Group events by day
					const eventsByDay = {};

					weekDays.forEach((day) => {
						// Initialize array for each day if it doesn't exist yet
						eventsByDay[format(day, 'yyyy-MM-dd')] = [];
					});

					// Step 4: Process each event and assign it to appropriate days
					Object.values(eventsInPresentWeek).forEach(
						(event: { eventId: number; ref: React.MutableRefObject<ReactNode> }) => {
							const { eventId, ref } = event;
							// Find the event's detail from the events state
							const eventDetails = events.find((event) => String(event.id) === String(eventId));

							if (!eventDetails) return;

							const { start, end } = eventDetails;
							// Iterate through each day in the current week to check if the event spans it
							weekDays.forEach((day) => {
								// check the event start & end date is between current week day
								if ((day >= start && day <= end) || isSameDay(day, start)) {
									// Add the event to the specific day's group
									eventsByDay[format(day, 'yyyy-MM-dd')].push({
										eventId,
										ref,
										day,
									});
								}
							});
						}
					);
					// Step 5: Check the y-position differences for each day
					const eventPositions = {};
					Object.keys(eventsByDay).forEach((dayKey) => {
						const eventsInDay = eventsByDay[dayKey];

						// Sort events by their y-position (top position in DOM)
						eventsInDay.sort((a, b) => {
							const rectA = a.ref.getBoundingClientRect();
							const rectB = b.ref.getBoundingClientRect();
							return rectA.top - rectB.top;
						});

						// Step 6: Compare consecutive events' y-positions
						for (let i = 1; i < eventsInDay.length; i++) {
							const prevEvent = eventsInDay[i - 1];
							const currEvent = eventsInDay[i];

							//skip positioning multiday events
							const eventData = events.find((e) => String(e.id) === String(currEvent.eventId));
							const isMultiDay = eventData && !isSameDay(eventData.start, eventData.end);
							if (isMultiDay) continue;
							//
							const prevRect = prevEvent.ref.getBoundingClientRect();
							const currRect = currEvent.ref.getBoundingClientRect();

							const gap = currRect.top - (prevRect.top + prevRect.height);

							if (gap > 20) {
								// Add this gap to the current event and all subsequent events
								if (!eventPositions[currEvent.eventId]) {
									eventPositions[currEvent.eventId] = gap;
								}
								// Apply this gap to all subsequent events
								for (let j = i + 1; j < eventsInDay.length; j++) {
									const subsequentEvent = eventsInDay[j];
									if (!eventPositions[subsequentEvent.eventId]) {
										eventPositions[subsequentEvent.eventId] = gap;
									}
								}
							}
						}
					});

					// Step 7: Update state with event positions that have gaps > 20px
					setEventPositions((prevPositions) => {
						return { ...prevPositions, ...eventPositions };
					});
				});
			}
		}, 0);
		return () => {
			if (timerId) clearInterval(timerId);
		};
	}, [events, currentView, currentMonth]);

	const contextValue = useMemo(() => {
		return { eventPositions, setEventPositions, eventRefs };
	}, [eventPositions, setEventPositions]);

	return <EventPositionContext.Provider value={contextValue}>{children}</EventPositionContext.Provider>;
};

export const useEventPositionContext = () => {
	const context = useContext(EventPositionContext);

	invariant(context, 'useEventContext must be used within an EventProvider');

	return context;
};
